import { useState } from 'react';
import {
  BarChart3,
  Camera,
  ChevronLeft,
  ChevronRight,
  Cloud,
  ClipboardList,
  GitBranch,
  ImageIcon,
  Layers,
  Palette,
  Phone,
  RefreshCw,
  Search,
  Shield,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  COMPANY_ADDRESS,
  COMPANY_NAME,
  SUPPORT_EMAIL,
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_PHONE_TEL,
} from '../constants/site';

const whyItems = [
  {
    title: 'TreeVault Cloud Services',
    text: 'Secure sync and backups so your research stays available across devices—restore with confidence after a hardware swap or OS reinstall.',
    Icon: Cloud,
  },
  {
    title: 'Charting & Reports',
    text: 'Pedigree, descendant, and fan charts with layouts ready to print or share, including options tuned for wall charts and handouts.',
    Icon: BarChart3,
  },
  {
    title: 'Media Management',
    text: 'Attach photos and documents with tagging and search that scales with your tree, so sources stay next to the people they support.',
    Icon: ImageIcon,
  },
  {
    title: 'Color Coding & Smart Matching',
    text: 'Spot duplicates and organize branches visually before you merge—fewer accidental merges, clearer lines of descent.',
    Icon: Palette,
  },
  {
    title: 'FamilySync & Hints',
    text: 'Bring discoveries into your workflow with hints that respect your pace, so you can review and accept on your own timeline.',
    Icon: RefreshCw,
  },
  {
    title: 'FamilySearch Integration',
    text: 'Search and attach records while keeping context inside your tree, then cite sources in a way that still makes sense months later.',
    Icon: Search,
  },
];

const keyFeatures = [
  { title: 'Pedigree View', desc: 'Classic ancestry layout', Icon: GitBranch },
  { title: 'Family View', desc: 'Households and relationships', Icon: Layers },
  { title: 'Name List', desc: 'Quick lookup for everyone', Icon: ClipboardList },
  { title: 'Individual Details', desc: 'Rich profiles per person', Icon: Camera },
  { title: 'Media Gallery', desc: 'Photos and documents together', Icon: ImageIcon },
  { title: 'Research Log', desc: 'Track sources and findings', Icon: Search },
];

const issues = [
  { title: 'Sync Issues', text: 'Cloud sync conflicts between devices or duplicate persons after a merge.', emoji: '🔄' },
  { title: 'Upgrade Errors', text: 'Install or update failures on Windows or Mac.', emoji: '⚙️' },
  { title: 'Tree Issues', text: 'Missing data or tree not opening after a move.', emoji: '🌳' },
  { title: 'Missing Media', text: 'Broken paths after copying folders.', emoji: '📁' },
  { title: 'Mobile Display', text: 'Layout or sync quirks on phones and tablets.', emoji: '📱' },
  { title: 'Updates Not Sticking', text: 'Edits not saving or reflecting everywhere.', emoji: '📋' },
  { title: 'GEDCOM & Export', text: 'Import/export surprises, character encoding, or lost notes in transfers.', emoji: '📤' },
];

const testimonials = [
  {
    quote:
      'Family Tree Maker is simply the best genealogy software available. It is intuitive, powerful, and has helped me discover so much about my family history.',
    name: 'John S.',
    place: 'San Jose, CA',
  },
  {
    quote:
      'I have used Family Tree Maker for years and the cloud sync feature is a game-changer for keeping my research consistent.',
    name: 'Mary K.',
    place: 'Austin, TX',
  },
  {
    quote:
      'The FamilySearch integration makes research so much easier—I have found ancestors I never knew existed.',
    name: 'Robert T.',
    place: 'Seattle, WA',
  },
];

function openLiveChat() {
  if (typeof window !== 'undefined' && window.LiveChatWidget) {
    try {
      window.LiveChatWidget.call('maximize');
      return;
    } catch {
      /* ignore */
    }
  }
  window.location.hash = 'contact';
}

function HomePageRedesigned() {
  const [ti, setTi] = useState(0);
  const pub = process.env.PUBLIC_URL || '';

  return (
    <div className="ftm-page">
      <Header />
      <main>
        <section id="home" className="ftm-hero" aria-labelledby="ftm-hero-title">
          <div className="ftm-hero-inner">
            <div className="ftm-hero-left">
              <div className="ftm-hero-left__top">
                <h1 id="ftm-hero-title">Family Tree Maker</h1>
                <p className="ftm-hero-lead">
                  More than genealogy software—Family Tree Maker is a trusted home for your family history. We combine
                  powerful tools with dedicated support so you can preserve and share your heritage with confidence.
                </p>
              </div>

              <div className="ftm-hero-highlights">
                <ul className="ftm-hero-list">
                  <li>Family Tree Maker Download &amp; Copies</li>
                  <li>Setup &amp; Installation Guides</li>
                  <li>Upgrade &amp; Migration Assistance</li>
                  <li>Backup &amp; Restore Guides</li>
                  <li>Sync Error &amp; Troubleshooting</li>
                  <li>No Internet Fix Guide</li>
                  <li>FamilySearch Integration Help</li>
                  <li>Media &amp; Document Management</li>
                  <li>TreeVault Cloud Support</li>
                </ul>
              </div>

              <div className="ftm-hero-actions">
                <a href={`tel:${SUPPORT_PHONE_TEL}`} className="ftm-hero-cta ftm-hero-cta--primary">
                  <Phone size={22} aria-hidden />
                  {SUPPORT_PHONE_DISPLAY}
                </a>
              </div>
            </div>

            <div className="ftm-hero-right">
              <div className="ftm-hero-visual">
                <div className="ftm-hero-visual__frame">
                  <img src={`${pub}/hero_bg.jpg`} alt="Historical family photograph" loading="eager" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="about" className="ftm-section ftm-section--white">
          <div className="container" style={{ width: 'min(1120px, calc(100% - 32px))', margin: '0 auto' }}>
            <div className="ftm-section-title">
              <span className="ftm-kicker">Welcome</span>
              <h2>Welcome to Family Tree Maker Helps</h2>
              <p>
                Whether you are beginning your first tree or maintaining thousands of people, we focus on practical
                steps: backup first, move files deliberately, then verify relationships, sources, and media.
              </p>
              <p style={{ marginTop: '1rem' }}>
                Our articles and phone support emphasize repeatable checklists—what to click, what to copy, and how to
                prove a fix worked—so you spend less time guessing and more time researching.
              </p>
            </div>
            <div className="ftm-section-title" style={{ marginTop: '3rem' }}>
              <span className="ftm-kicker">Why choose us</span>
              <h2>Why Choose Family Tree Maker Helps</h2>
            </div>
            <div className="ftm-grid-3">
              {whyItems.map(({ title, text, Icon }) => (
                <article key={title} className="ftm-card-icon">
                  <div className="ftm-icon-wrap">
                    <Icon size={24} strokeWidth={1.75} aria-hidden />
                  </div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="ftm-banner-support">
          <h2>Expert Support When You Need It</h2>
          <p>
            Need help with installation, migration, or learning the software? Our guides and support resources walk you
            through each step—without losing your work. Ask about version differences, cloud sync, or restoring from a
            backup before you delete your old computer.
          </p>
          <div className="ftm-banner-actions">
            <a href={`tel:${SUPPORT_PHONE_TEL}`} className="ftm-btn-outline-white">
              <Phone size={18} />
              Call {SUPPORT_PHONE_DISPLAY}
            </a>
            <button type="button" className="ftm-btn-dark" onClick={openLiveChat}>
              Chat Now
            </button>
          </div>
        </section>

        <section id="smart" className="ftm-section ftm-section--mint">
          <div className="container" style={{ width: 'min(1120px, calc(100% - 32px))', margin: '0 auto' }}>
            <div className="ftm-section-title">
              <span className="ftm-kicker">Seamless transition</span>
              <h2>Smart Solutions for Your Family History</h2>
              <p>
                Moving between Windows and Mac, or upgrading hardware? Plan your backup, copy the full media folder,
                then restore and verify before you remove old files.
              </p>
              <p style={{ marginTop: '1rem' }}>
                If you use external drives or NAS storage, we also cover path consistency—so thumbnails and document links
                survive the move instead of turning into broken shortcuts.
              </p>
            </div>
          </div>
        </section>

        <section id="guides" className="ftm-section ftm-section--white">
          <div className="container" style={{ width: 'min(900px, calc(100% - 32px))', margin: '0 auto' }}>
            <div className="ftm-section-title">
              <span className="ftm-kicker">Promote FTM</span>
              <h2>Transfer FTM to a New Computer</h2>
              <p>
                A straightforward sequence many genealogists use when moving Family Tree Maker. Follow it in order; skip
                steps only after you have verified the previous step on the new machine.
              </p>
            </div>
            <ul className="ftm-shield-list">
              {[
                'Create a full backup on your current computer and confirm it opens.',
                'Install the same major version on the new system.',
                'Restore from backup; copy your entire media folder to matching paths when possible.',
                'Activate or sign in, then spot-check people, sources, and a sample of media links.',
                'Run a compact backup on the new PC after validation, and store a copy off-site (cloud or second drive).',
              ].map((line) => (
                <li key={line}>
                  <Shield size={20} color="#2e7d32" style={{ flexShrink: 0 }} aria-hidden />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="ftm-section ftm-section--mint">
          <div className="container ftm-prose" style={{ width: 'min(900px, calc(100% - 32px))', margin: '0 auto' }}>
            <h2>What is Family Tree Maker Helps?</h2>
            <p>
              We publish independent, plain-language guidance for people using Family Tree Maker: organizing research,
              managing media, and avoiding the most common migration mistakes—not hype, just repeatable steps.
            </p>
            <p>
              Content here is for education and troubleshooting context; it does not replace the official documentation
              shipped with your software or updates from the publisher. When in doubt, complete a fresh backup before
              changing settings or merging trees.
            </p>
            <h2>Who we help</h2>
            <p>
              Hobbyists tracing a few generations, society volunteers maintaining shared lines, and advanced researchers
              with large media libraries all use the same fundamentals: consistent file paths, dated backups, and notes
              that explain where a fact came from.
            </p>
            <h2>Support</h2>
            <p>
              For hands-on questions, include your OS, software version, and what you already tried. If something fails
              after a move, mention whether the media folder was copied and where it lives on disk.
            </p>
            <p>
              Screenshots of error text (or the exact wording), plus whether you are on Wi‑Fi or Ethernet for sync, help
              us narrow down fixes faster—especially for activation and cloud-related issues.
            </p>
          </div>
        </section>

        <section id="features" className="ftm-section ftm-section--white">
          <div className="container" style={{ width: 'min(1120px, calc(100% - 32px))', margin: '0 auto' }}>
            <div className="ftm-section-title">
              <span className="ftm-kicker">Views</span>
              <h2>Key Features at a Glance</h2>
            </div>
            <div className="ftm-grid-3">
              {keyFeatures.map(({ title, desc, Icon }) => (
                <article key={title} className="ftm-card-icon">
                  <div className="ftm-icon-wrap">
                    <Icon size={24} strokeWidth={1.75} />
                  </div>
                  <h3>{title}</h3>
                  <p>{desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="requirements" className="ftm-req-section">
          <h2>System Requirements</h2>
          <div className="ftm-req-grid">
            <article className="ftm-req-card">
              <h3>Windows</h3>
              <ul>
                <li>Windows 10 or later (64-bit)</li>
                <li>2 GHz Intel or AMD processor</li>
                <li>8 GB RAM (16 GB recommended)</li>
                <li>2 GB free disk space; 1280×800 display</li>
                <li>Internet for activation and updates</li>
              </ul>
            </article>
            <article className="ftm-req-card">
              <h3>macOS</h3>
              <ul>
                <li>macOS 12 (Monterey) or later</li>
                <li>Intel Core i5 or Apple Silicon</li>
                <li>8 GB RAM (16 GB recommended)</li>
                <li>2 GB free disk space; 1280×800 display</li>
                <li>Internet for activation and updates</li>
              </ul>
            </article>
          </div>
        </section>

        <section className="ftm-section ftm-section--mint">
          <div className="container" style={{ width: 'min(900px, calc(100% - 32px))', margin: '0 auto' }}>
            <div className="ftm-section-title">
              <span className="ftm-kicker">Stories</span>
              <h2>What Our Customers Say</h2>
            </div>
            <div className="ftm-quote-box">
              <div className="ftm-stars" aria-hidden>
                ★★★★★
              </div>
              <p style={{ fontSize: '1.18rem', fontStyle: 'italic', color: 'var(--ftm-muted)', margin: 0, lineHeight: 1.65 }}>
                “{testimonials[ti].quote}”
              </p>
              <p style={{ margin: '1rem 0 0', fontWeight: 700, color: 'var(--ftm-text)' }}>{testimonials[ti].name}</p>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.9rem', color: 'var(--ftm-forest-light)' }}>
                {testimonials[ti].place}
              </p>
              <div className="ftm-quote-nav">
                <button type="button" aria-label="Previous" onClick={() => setTi((i) => (i - 1 + testimonials.length) % testimonials.length)}>
                  <ChevronLeft size={22} />
                </button>
                <button type="button" aria-label="Next" onClick={() => setTi((i) => (i + 1) % testimonials.length)}>
                  <ChevronRight size={22} />
                </button>
              </div>
            </div>
          </div>
        </section>

        <section id="issues" className="ftm-section ftm-section--white">
          <div className="container" style={{ width: 'min(1120px, calc(100% - 32px))', margin: '0 auto' }}>
            <div className="ftm-section-title">
              <span className="ftm-kicker">Help desk</span>
              <h2>Common Issues We Help With</h2>
              <p>Start with the symptom that matches yours—then walk through backup → transfer → verify.</p>
            </div>
            <div className="ftm-grid-issues">
              {issues.map((it) => (
                <article key={it.title} className="ftm-issue-card">
                  <div className="ftm-issue-icon" aria-hidden>
                    {it.emoji}
                  </div>
                  <h3>{it.title}</h3>
                  <p>{it.text}</p>
                </article>
              ))}
            </div>
            <div className="ftm-center-cta">
              <a href={`tel:${SUPPORT_PHONE_TEL}`} className="ftm-btn-primary-lg">
                <Phone size={20} />
                Contact Us Now
              </a>
            </div>
          </div>
        </section>

        <section id="blog" className="ftm-section ftm-section--mint">
          <div className="container ftm-prose" style={{ width: 'min(800px, calc(100% - 32px))', margin: '0 auto', textAlign: 'center' }}>
            <span className="ftm-kicker">Blog</span>
            <h2 style={{ color: 'var(--ftm-text)' }}>Tips &amp; updates</h2>
            <p>
              We publish short articles on backups, media folders, and upgrade paths. Use the contact section below to
              suggest a topic or ask a question for a future post.
            </p>
            <p style={{ marginTop: '1rem' }}>
              Recent themes include avoiding duplicate profiles after a GEDCOM import, organizing sources so citations
              stay readable, and what to verify first when sync pauses or stalls on one device only.
            </p>
          </div>
        </section>

        <section id="contact" className="ftm-section ftm-section--white">
          <div className="container" style={{ width: 'min(1000px, calc(100% - 32px))', margin: '0 auto' }}>
            <div className="ftm-section-title">
              <span className="ftm-kicker">Contact</span>
              <h2>Get in Touch</h2>
              <p>
                Tell us your OS, software version, and what you expected to happen. Add whether you moved the{' '}
                <strong>media folder</strong>.
              </p>
              <p style={{ marginTop: '0.75rem' }}>
                If you are scheduling a longer restore or migration, mention your time zone and whether you prefer a
                callback or email first—we reply as quickly as we can during business hours.
              </p>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1.1fr',
                gap: '2rem',
                alignItems: 'start',
              }}
              className="ftm-contact-grid"
            >
              <div
                style={{
                  background: '#f4faf6',
                  border: '1px solid #cfe5d8',
                  borderRadius: '12px',
                  padding: '1.25rem 1.5rem',
                }}
              >
                <p style={{ margin: '0 0 8px' }}>
                  <strong>{COMPANY_NAME}</strong>
                </p>
                <p style={{ margin: '0 0 8px' }}>{COMPANY_ADDRESS}</p>
                <p style={{ margin: '0 0 8px' }}>
                  Phone: <a href={`tel:${SUPPORT_PHONE_TEL}`}>{SUPPORT_PHONE_DISPLAY}</a>
                </p>
                <p style={{ margin: 0 }}>
                  Email: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                </p>
              </div>
              <form
                style={{ display: 'grid', gap: '12px' }}
                onSubmit={(e) => {
                  e.preventDefault();
                  const phone = String(new FormData(e.currentTarget).get('phone') || '').trim();
                  if (!phone) {
                    window.alert('Please enter your phone number.');
                    return;
                  }
                }}
              >
                <input type="text" name="name" placeholder="Your Name" required style={{ padding: '12px', borderRadius: '8px', border: '1px solid #b9d0c3' }} />
                <input type="email" name="email" placeholder="Your Email" required style={{ padding: '12px', borderRadius: '8px', border: '1px solid #b9d0c3' }} />
                <input type="tel" name="phone" placeholder="Your Phone" required style={{ padding: '12px', borderRadius: '8px', border: '1px solid #b9d0c3' }} />
                <textarea name="message" placeholder="Your message" rows={5} required style={{ padding: '12px', borderRadius: '8px', border: '1px solid #b9d0c3' }} />
                <button type="submit" className="ftm-btn-primary-lg" style={{ width: 'fit-content', border: 'none', cursor: 'pointer' }}>
                  Submit Request
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <style>{`
        @media (max-width: 768px) {
          .ftm-contact-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

export default HomePageRedesigned;
